import { AccountAnalysisState } from '@core/model/domain/accountAnalysisState';
import { Bad } from '@core/model/framework/Result/BadResult';
import { OkVal } from '@core/model/framework/Result/OkResult';
import { Usecase } from '@core/model/framework/usecase';
import type { WaitOnCaseStateNotificationGateway } from '@core/model/gateway/waitOnCaseStateNotificationGateway';
import type { WaitOnBankAccountAnalysisCompletionGateway } from '@core/model/gateway/waitOnBankAccountAnalysisCompletionGateway';
import type { AccountService } from '@core/model/service/accountService';
import { SERVICE_ID } from '@di/serviceId';
import { inject, injectable } from 'inversify';

export type WaitOnBankAccountAnalysisCompletionInput = {
  userId: string;
  userToken: string;
  connectionId: string;
  selectedAccountsId: string[];
};
export type WaitOnBankAccountAnalysisCompletionOutput = Promise<
  | OkVal<AccountAnalysisState>
  | Bad<
      | 'CASE_IN_INCORRECT_STATUS'
      | 'WAITED_SYNCHRONIZATION_COMPLETION_FOR_TOO_LONG'
      | 'STOPPED_POLLING_VIA_USER_ACTION'
    >
>;

@injectable()
export class WaitOnBankAccountAnalysisCompletion
  implements Usecase<WaitOnBankAccountAnalysisCompletionInput, WaitOnBankAccountAnalysisCompletionOutput>
{
  constructor(
    @inject(SERVICE_ID.accountService)
    private readonly accountService: AccountService,
    @inject(SERVICE_ID.waitOnBankAccountAnalysisCompletionGateway)
    private readonly waitOnBankAccountAnalysisCompletionGateway: WaitOnBankAccountAnalysisCompletionGateway,
    @inject(SERVICE_ID.waitOnCaseStateNotificationGateway)
    private readonly waitOnCaseStateNotificationGateway: WaitOnCaseStateNotificationGateway
  ) {}

  async execute(cmd: WaitOnBankAccountAnalysisCompletionInput): WaitOnBankAccountAnalysisCompletionOutput {
    const { userId, userToken, connectionId, selectedAccountsId } = cmd;

    await this.accountService.synchronizeAccounts({
      userToken,
      userId,
      connectionId,
      selectedAccountsId,
    });

    const waitOnBankAccountAnalysisCompletionResult =
      await this.waitOnBankAccountAnalysisCompletionGateway.await({
        userToken,
        userId,
        bankAccountsIds: selectedAccountsId,
      });

    if (waitOnBankAccountAnalysisCompletionResult.success === false) {
      return waitOnBankAccountAnalysisCompletionResult;
    }

    const caseStateResult = await this.waitOnCaseStateNotificationGateway.awaitCaseStateUpdate({
      userId,
      userToken,
    });

    return caseStateResult;
  }
}
